import React, { useState, useEffect } from 'react';
import { Calendar, MessageSquare, Clock, CheckCircle, AlertCircle, Loader2 } from 'lucide-react';
import { SupportTicket, SupportStatus } from '../../types';
import { supportApi } from '../../services/supportApi';
import toast from 'react-hot-toast';

interface SupportTicketHistoryProps {
  onTicketSelect?: (ticket: SupportTicket) => void;
}

export default function SupportTicketHistory({ onTicketSelect }: SupportTicketHistoryProps) {
  const [tickets, setTickets] = useState<SupportTicket[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedTicketId, setSelectedTicketId] = useState<string | null>(null);

  useEffect(() => {
    loadTickets();
  }, []);

  const loadTickets = async () => {
    try {
      setIsLoading(true);
      const userTickets = await supportApi.getUserTickets();
      setTickets(userTickets);
    } catch (error) {
      console.error('Failed to load tickets:', error);
      toast.error('Failed to load your support tickets');
    } finally {
      setIsLoading(false);
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case SupportStatus.Open:
        return <AlertCircle className="w-4 h-4 text-blue-500" />;
      case SupportStatus.InProgress:
        return <Clock className="w-4 h-4 text-yellow-500" />;
      case SupportStatus.WaitingForUser:
        return <MessageSquare className="w-4 h-4 text-orange-500" />;
      case SupportStatus.Resolved:
      case SupportStatus.Closed:
        return <CheckCircle className="w-4 h-4 text-green-500" />;
      default:
        return <AlertCircle className="w-4 h-4 text-gray-500" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case SupportStatus.Open:
        return 'bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-300';
      case SupportStatus.InProgress:
        return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-300';
      case SupportStatus.WaitingForUser:
        return 'bg-orange-100 text-orange-800 dark:bg-orange-900/20 dark:text-orange-300';
      case SupportStatus.Resolved:
      case SupportStatus.Closed:
        return 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-300';
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-900/20 dark:text-gray-300';
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleTicketClick = (ticket: SupportTicket) => {
    setSelectedTicketId(ticket.id);
    onTicketSelect?.(ticket);
  };

  if (isLoading) {
    return (
      <div className="p-6 flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-blue-600" />
        <span className="ml-2 text-gray-600 dark:text-gray-300">Loading your tickets...</span>
      </div>
    );
  }

  if (tickets.length === 0) {
    return (
      <div className="p-6 text-center">
        <MessageSquare className="w-12 h-12 text-gray-400 mx-auto mb-4" />
        <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">
          No Support Tickets
        </h3>
        <p className="text-gray-600 dark:text-gray-400">
          You haven't submitted any support tickets yet. When you do, they'll appear here.
        </p>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="mb-6">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">
          Your Support Tickets
        </h3>
        <p className="text-sm text-gray-600 dark:text-gray-400">
          View and track the status of your support requests.
        </p>
      </div>

      <div className="space-y-4">
        {tickets.map((ticket) => (
          <div
            key={ticket.id}
            onClick={() => handleTicketClick(ticket)}
            className={`p-4 border border-gray-200 dark:border-gray-700 rounded-lg cursor-pointer 
                       transition-all duration-200 hover:shadow-md hover:border-blue-300 dark:hover:border-blue-600
                       ${selectedTicketId === ticket.id 
                         ? 'ring-2 ring-blue-500 border-blue-500' 
                         : ''}`}
          >
            <div className="flex items-start justify-between mb-3">
              <div className="flex-1 min-w-0">
                <div className="flex items-center mb-2">
                  {getStatusIcon(ticket.status)}
                  <span className={`ml-2 px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(ticket.status)}`}>
                    {ticket.status}
                  </span>
                  <span className="ml-2 px-2 py-1 text-xs font-medium rounded-full bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-300">
                    {ticket.category}
                  </span>
                </div>
                <h4 className="text-base font-medium text-gray-900 dark:text-gray-100 truncate">
                  {ticket.subject}
                </h4>
              </div>
            </div>

            <p className="text-sm text-gray-600 dark:text-gray-400 mb-3 line-clamp-2">
              {ticket.description}
            </p>

            <div className="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400">
              <div className="flex items-center">
                <Calendar className="w-4 h-4 mr-1" />
                <span>Created {formatDate(ticket.createdAt)}</span>
              </div>
              
              {ticket.updatedAt && (
                <div className="flex items-center">
                  <Clock className="w-4 h-4 mr-1" />
                  <span>Updated {formatDate(ticket.updatedAt)}</span>
                </div>
              )}
            </div>

            {ticket.resolution && (
              <div className="mt-3 p-3 bg-green-50 dark:bg-green-900/20 rounded-md">
                <h5 className="text-sm font-medium text-green-800 dark:text-green-300 mb-1">
                  Resolution
                </h5>
                <p className="text-sm text-green-700 dark:text-green-400">
                  {ticket.resolution}
                </p>
                {ticket.resolvedAt && (
                  <p className="text-xs text-green-600 dark:text-green-500 mt-1">
                    Resolved on {formatDate(ticket.resolvedAt)}
                  </p>
                )}
              </div>
            )}
          </div>
        ))}
      </div>

      <div className="mt-6 text-center">
        <button
          onClick={loadTickets}
          className="text-sm text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 
                   font-medium transition-colors duration-200"
        >
          Refresh Tickets
        </button>
      </div>
    </div>
  );
}
